import React, { useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { AuthContext } from '../../contexts/AuthContext';

/**
 * Composant pour protéger une route avec une permission spécifique
 * @param {Object} props
 * @param {React.ReactNode} props.children - Composants enfants à rendre si autorisé
 * @param {string|string[]} props.permission - Permission(s) requise(s)
 * @param {string} props.role - Rôle requis (optionnel)
 * @param {boolean} props.requireAll - Si true, toutes les permissions sont requises
 * @param {string} props.fallbackPath - Chemin de redirection si non autorisé
 * @param {React.ReactNode} props.fallback - Composant à afficher si non autorisé
 */
const ProtectedRoute = ({ 
  children, 
  permission, 
  role,
  requireAll = false,
  fallbackPath = '/dashboard',
  fallback 
}) => {
  const { user, hasPermission, hasRole } = usePermissions();
  const { loading } = useContext(AuthContext);
  const location = useLocation();

  // Tant que checkAuth n'a pas répondu, `user` est encore nul : rediriger ici
  // éjectait vers /login tout accès direct (ou rechargement) d'une page
  // protégée par permission, alors que la session était valide.
  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-line border-t-brand" />
      </div>
    );
  }

  // Vérifier si l'utilisateur est connecté
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Vérifier le rôle si spécifié
  if (role && !hasRole(role)) {
    if (fallback) {
      return fallback;
    }
    return <Navigate to={fallbackPath} replace />;
  }

  // Vérifier les permissions si spécifiées
  if (permission) {
    const permissions = Array.isArray(permission) ? permission : [permission];
    
    let authorized = false;
    
    if (requireAll) {
      // Toutes les permissions doivent être présentes
      authorized = permissions.every(perm => hasPermission(perm));
    } else {
      // Au moins une permission doit être présente
      authorized = permissions.some(perm => hasPermission(perm));
    }

    if (!authorized) {
      if (fallback) {
        return fallback;
      }
      return <Navigate to={fallbackPath} replace />;
    }
  }

  return children;
};

/**
 * Composant pour afficher des éléments basés sur les permissions
 * @param {Object} props
 * @param {React.ReactNode} props.children - Contenu à afficher si autorisé
 * @param {string|string[]} props.permission - Permission(s) requise(s)
 * @param {string} props.role - Rôle requis (optionnel)
 * @param {boolean} props.requireAll - Si true, toutes les permissions sont requises
 * @param {React.ReactNode} props.fallback - Contenu à afficher si non autorisé
 */
export const PermissionGuard = ({ 
  children, 
  permission, 
  role,
  requireAll = false,
  fallback = null 
}) => {
  const { user, hasPermission, hasRole } = usePermissions();

  // Si pas d'utilisateur, ne rien afficher
  if (!user) {
    return fallback;
  }

  // Vérifier le rôle si spécifié
  if (role && !hasRole(role)) {
    return fallback;
  }

  // Vérifier les permissions si spécifiées
  if (permission) {
    const permissions = Array.isArray(permission) ? permission : [permission];
    
    let authorized = false;
    
    if (requireAll) {
      authorized = permissions.every(perm => hasPermission(perm));
    } else {
      authorized = permissions.some(perm => hasPermission(perm));
    }

    if (!authorized) {
      return fallback;
    }
  }

  return children;
};

/**
 * Composant pour créer des boutons conditionnels basés sur les permissions
 * @param {Object} props
 * @param {string|string[]} props.permission - Permission(s) requise(s)
 * @param {string} props.role - Rôle requis (optionnel)
 * @param {boolean} props.requireAll - Si true, toutes les permissions sont requises
 * @param {Function} props.onClick - Fonction appelée au clic
 * @param {string} props.className - Classes CSS
 * @param {boolean} props.disabled - Si le bouton est désactivé
 * @param {React.ReactNode} props.children - Contenu du bouton
 * @param {Object} props.buttonProps - Props supplémentaires pour le bouton
 */
export const PermissionButton = ({ 
  permission, 
  role,
  requireAll = false,
  onClick, 
  className = '', 
  disabled = false,
  children,
  ...buttonProps 
}) => {
  const { user, hasPermission, hasRole } = usePermissions();

  // Vérifier les autorisations
  let authorized = true;

  if (!user) {
    authorized = false;
  }

  if (role && !hasRole(role)) {
    authorized = false;
  }

  if (permission) {
    const permissions = Array.isArray(permission) ? permission : [permission];
    
    if (requireAll) {
      authorized = permissions.every(perm => hasPermission(perm));
    } else {
      authorized = permissions.some(perm => hasPermission(perm));
    }
  }

  // Ne pas rendre le bouton si non autorisé
  if (!authorized) {
    return null;
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={className}
      {...buttonProps}
    >
      {children}
    </button>
  );
};

/**
 * Composant HOC pour wrapper des composants avec protection de permissions
 * @param {React.Component} Component - Composant à protéger
 * @param {Object} permissions - Configuration des permissions
 * @returns {React.Component}
 */
export const withPermissions = (Component, { permission, role, requireAll = false, fallback = null }) => {
  return function ProtectedComponent(props) {
    return (
      <PermissionGuard 
        permission={permission} 
        role={role}
        requireAll={requireAll}
        fallback={fallback}
      >
        <Component {...props} />
      </PermissionGuard>
    );
  };
};

/**
 * Composants spécialisés pour les modules principaux
 */

// Protection pour les paiements
export const PaymentsProtectedRoute = ({ children, action = 'read', ...props }) => (
  <ProtectedRoute permission={`payments.${action}`} {...props}>
    {children}
  </ProtectedRoute>
);

// Protection pour les enseignants
export const TeachersProtectedRoute = ({ children, action = 'read', ...props }) => (
  <ProtectedRoute permission={`teachers.${action}`} {...props}>
    {children}
  </ProtectedRoute>
);

// Protection pour les élèves
export const StudentsProtectedRoute = ({ children, action = 'read', ...props }) => (
  <ProtectedRoute permission={`students.${action}`} {...props}>
    {children}
  </ProtectedRoute>
);

// Protection pour les bulletins
export const BulletinsProtectedRoute = ({ children, action = 'read', ...props }) => (
  <ProtectedRoute permission={`bulletins.${action}`} {...props}>
    {children}
  </ProtectedRoute>
);

// Protection pour l'administration
export const AdminProtectedRoute = ({ children, ...props }) => (
  <ProtectedRoute role="administrateur" {...props}>
    {children}
  </ProtectedRoute>
);

export default ProtectedRoute;